import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Repeat, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import CustomerMasterPicker from "./CustomerMasterPicker";

const CADENCES = [
    { v: "weekly", label: "Every week" },
    { v: "fortnightly", label: "Every 2 weeks" },
    { v: "monthly", label: "Every month (recommended)" },
    { v: "every_60_days", label: "Every 60 days" },
    { v: "every_90_days", label: "Every 90 days" },
];

export default function SubscribeButton({ product, variant }) {
    const { user } = useAuth();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        quantity: 1,
        cadence: "monthly",
        name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "",
    });
    const [busy, setBusy] = useState(false);
    // Master link — staff (admin/editor/MR/accounts/packing) can pick a saved customer
    // to auto-fill the entire delivery address. Customers don't see this picker.
    const [masterCustomer, setMasterCustomer] = useState(null);
    const staffCanPickMaster = user && (
        user.role === "admin" || user.role === "editor" ||
        (user.role === "employee" && (user.capabilities || []).some(c => ["mr", "accounts", "packing", "admin"].includes(c)))
    );

    if (!variant) return null;

    const start = () => {
        if (!user) { nav("/login"); return; }
        setOpen(true);
    };

    const pickFromMaster = (c) => {
        setMasterCustomer(c);
        if (!c) return;
        setForm(f => ({
            ...f,
            name: c.name || f.name,
            phone: c.phone || f.phone,
            line1: c.address_line1 || f.line1,
            line2: c.address_line2 || f.line2,
            city: c.city || f.city,
            state: c.state || f.state,
            pincode: c.pincode || f.pincode,
        }));
    };

    const saveAsNew = async () => {
        if (!form.name.trim()) return toast.error("Type the customer name first");
        try {
            const { data } = await api.post("/customers", {
                name: form.name,
                phone: form.phone,
                customer_type: "retailer",
                address_line1: form.line1,
                address_line2: form.line2,
                city: form.city,
                state: form.state,
                pincode: form.pincode,
            });
            setMasterCustomer(data);
            toast.success(`Saved "${data.name}" to customer master`);
        } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone || !form.line1 || !form.city || !form.state || !form.pincode) {
            toast.error("Fill in all delivery fields");
            return;
        }
        setBusy(true);
        try {
            await api.post("/subscriptions", {
                product_id: product.id,
                size_id: variant.size_id,
                quantity: Number(form.quantity) || 1,
                cadence: form.cadence,
                address: {
                    name: form.name, phone: form.phone, line1: form.line1, line2: form.line2,
                    city: form.city, state: form.state, pincode: form.pincode,
                },
                payment_method: "cod",
                customer_master_id: masterCustomer?.id || null,
            });
            toast.success("Auto-refill set up! Manage it any time from My account.");
            setOpen(false);
            nav("/subscriptions");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not create subscription");
        } finally { setBusy(false); }
    };

    return (
        <>
            <Button
                type="button"
                onClick={start}
                variant="outline"
                className="rounded-none border-sky-700 text-sky-700 hover:bg-sky-50 inline-flex items-center gap-1"
                data-testid="subscribe-btn"
            >
                <Repeat className="w-4 h-4" />Auto-refill
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2"><Repeat className="w-5 h-5 text-sky-700" />Set up auto-refill</DialogTitle>
                        <DialogDescription className="text-xs text-stone-500">{product.name} · {variant.size_label}. Pause, change frequency or cancel any time.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <L label="Quantity per refill"><Input type="number" min="1" max="20" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} className="rounded-none border-stone-300" data-testid="sub-qty" /></L>
                            <L label="Frequency">
                                <Select value={form.cadence} onValueChange={v => setForm({ ...form, cadence: v })}>
                                    <SelectTrigger className="rounded-none border-stone-300" data-testid="sub-cadence-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>{CADENCES.map(c => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </L>
                        </div>
                        <div className="border-t border-stone-200 pt-3">
                            <div className="label-eyebrow text-stone-500 mb-2">Delivery address</div>
                            {staffCanPickMaster && (
                                <div className="mb-3">
                                    <CustomerMasterPicker
                                        value={masterCustomer}
                                        onPick={pickFromMaster}
                                        onSaveAsNew={saveAsNew}
                                        dataTestIdPrefix="sub-master"
                                    />
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                <L label="Full name"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-none border-stone-300" data-testid="sub-name" /></L>
                                <L label="Phone"><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="rounded-none border-stone-300" data-testid="sub-phone" /></L>
                            </div>
                            <L label="Address line 1"><Input value={form.line1} onChange={e => setForm({ ...form, line1: e.target.value })} className="rounded-none border-stone-300 mt-3" /></L>
                            <L label="Address line 2 (optional)"><Input value={form.line2} onChange={e => setForm({ ...form, line2: e.target.value })} className="rounded-none border-stone-300 mt-3" /></L>
                            <div className="grid grid-cols-3 gap-3 mt-3">
                                <L label="City"><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="rounded-none border-stone-300" /></L>
                                <L label="State"><Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} className="rounded-none border-stone-300" /></L>
                                <L label="Pincode"><Input value={form.pincode} onChange={e => setForm({ ...form, pincode: e.target.value })} className="rounded-none border-stone-300" /></L>
                            </div>
                        </div>
                        <div className="text-[11px] text-stone-500 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-emerald-600" />No commitment · pause or cancel anytime · Cash on Delivery.</div>
                        <DialogFooter>
                            <Button type="button" variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="sub-submit">{busy ? "Setting up…" : "Start auto-refill"}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}

function L({ label, children }) {
    return <label className="block"><Label className="text-[10px] uppercase tracking-widest text-stone-500">{label}</Label>{children}</label>;
}
